import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {IconButton, Menu, MenuItem, Tooltip, Typography} from '@mui/material'
import React, {useCallback, useMemo, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import styled, {css} from 'styled-components'
import {agencyApi, useLazyGetOneAgencyQuery} from '~/src/store/apis/agency-api'
import {selectAgencyState} from '~/src/store/slices/agency-slice'
import {updateLoggedInAgencyId, setBackendRolePermissions} from '~/src/store/slices/root-slice'
import {useAppDispatch, useAppSelector} from '~/src/store/store-hooks'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {roleApi} from '~/src/services'
import {companyApi} from '~/src/store/apis/company-api'
import {serviceOrderApi} from '~/src/store/apis/service-order-api'
import {jobOrderApi} from '~/src/store/apis/job-order-api'
import {applicantsApi} from '~/src/store/apis/applicants-api'
import {clearApplicantsState} from '~/src/store/slices/applicants-slice'
import {userApi} from '~/src/store/apis/user-api'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {getRouteForRole, type ModulePermission} from '~/src/utils/routeUtils'

const StyledMenuItem = styled(MenuItem)(
  ({theme}) => css`
    width: 100%;
    &:hover .itemText {
      color: ${theme.palette.primary.main};
    }
  `,
)

const StyledText = styled(Typography)<{$selected: boolean}>(
  ({theme, $selected}) => css`
    left: 9.62%;
    right: 17.79%;
    top: 20.27%;
    bottom: 71.17%;
    font-style: normal;
    :400 ;
    font-weight: ${$selected ? 600 : 400};
    font-size: 14px;
    line-height: 19px;
    color: ${$selected ? theme.palette.primary.main : '#383838'};
  `,
)

const StyledTitleText = styled(Typography)`
  font-weight: 600;
  font-size: 14px;
  color: #334d6e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

interface IUserMenuProps {
  sx?: object
}

const SelectAgencyMenu = ({sx = {}}: IUserMenuProps) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {agencyNamesAndIds} = useAppSelector(selectAgencyState)
  const [getAgency, {error: agencyError}] = useLazyGetOneAgencyQuery()
  const {loggedInAgencyId} = useAppSelector((state) => state.root)
  const {currentUser} = useCurrentUser()
  const {showError} = useGlobalSnackbar()

  // Determine which agencies to show:
  // 1. If user has agencyAssignments (multi-agency), use those
  // 2. Otherwise, fall back to general admin's agencyNamesAndIds
  const availableAgencies = useMemo(() => {
    if (currentUser?.agencyAssignments && currentUser.agencyAssignments.length > 0) {
      return currentUser.agencyAssignments.map((assignment) => ({
        id: assignment.agencyId,
        name: assignment.agencyName || assignment.agencyId, // Fallback to ID if name not available
      }))
    }
    // Fall back to agencyNamesAndIds for general_admin users
    return agencyNamesAndIds
  }, [currentUser?.agencyAssignments, agencyNamesAndIds])

  const selectedName = availableAgencies.find((datum) => datum.id === loggedInAgencyId)?.name

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget)
  }

  const handleCloseUserMenu = useCallback(() => {
    setAnchor(null)
  }, [setAnchor])

  // Helper to update permissions when switching agencies for multi-agency users
  // Returns the new permissions and role name for routing
  const updateAgencyPermissions = useCallback(
    async (
      agencyId: string,
    ): Promise<{roleName: string; modulePermissions: ModulePermission[]} | undefined> => {
      if (!currentUser?.agencyAssignments || currentUser.agencyAssignments.length === 0) return

      const permissionsResponse = await roleApi.getPermissionsForRoles(
        currentUser.roles as string[],
      )

      const primaryRole = permissionsResponse.roles[0]
      if (!primaryRole) return

      // Check if this agency has custom permissions
      const selectedAssignment = currentUser.agencyAssignments.find((a) => a.agencyId === agencyId)

      // Use agency-specific permissions if they exist and are non-empty
      const hasCustomPermissions =
        selectedAssignment?.modulePermissions && selectedAssignment.modulePermissions.length > 0

      const modulePermissions = hasCustomPermissions
        ? (selectedAssignment.modulePermissions as ModulePermission[])
        : (primaryRole.modulePermissions as ModulePermission[])

      dispatch(
        setBackendRolePermissions({
          _id: primaryRole.id,
          id: primaryRole.id,
          name: primaryRole.name,
          accessList: primaryRole.accessList,
          modulePermissions: modulePermissions,
          isPlatform: primaryRole.category === 'Platform',
          // Store category for layout variant determination (custom roles need this)
          category: primaryRole.category as 'Platform' | 'MSP' | 'Agency' | 'Company',
          application: 'msp',
          createdDate: primaryRole.createdDate?.toString() ?? '',
          modifiedDate: primaryRole.modifiedDate?.toString() ?? '',
          versionKey: Number(primaryRole.versionKey) || 0,
        }),
      )

      return {roleName: primaryRole.name, modulePermissions}
    },
    [currentUser, dispatch],
  )

  const handleClick = useCallback(
    async (id: string) => {
      try {
        const agency = await getAgency(id).unwrap()

        dispatch(updateLoggedInAgencyId(agency.id))
        setAnchor(null)

        // Update permissions for multi-agency users when switching agencies
        let permissionsResult: {roleName: string; modulePermissions: ModulePermission[]} | undefined
        try {
          permissionsResult = await updateAgencyPermissions(id)
        } catch (permError) {
          console.error('Failed to update permissions for agency:', permError)
          showError('Failed to update permissions. Please refresh the page.')
        }

        // Reset all API caches to ensure fresh data for the new agency context
        dispatch(companyApi.util.resetApiState())
        dispatch(serviceOrderApi.util.resetApiState())
        dispatch(jobOrderApi.util.resetApiState())
        // Clear applicants slice state first (prevents stale candidate data showing)
        dispatch(clearApplicantsState())
        dispatch(applicantsApi.util.resetApiState())
        dispatch(userApi.util.resetApiState())
        dispatch(agencyApi.util.resetApiState())

        // Navigate to first accessible route based on new agency's permissions
        // If permissions were updated, use them to determine the route
        // Otherwise, fall back to using the user's role
        const targetRoute = permissionsResult
          ? getRouteForRole(permissionsResult.roleName, permissionsResult.modulePermissions)
          : getRouteForRole(currentUser?.roles?.[0] || '')
        navigate(targetRoute)
      } catch (switchError) {
        console.error('Failed to switch agency:', switchError)
        showError('Failed to switch agency. Please try again.')
      }
    },
    [currentUser?.roles, dispatch, getAgency, navigate, showError, updateAgencyPermissions],
  )

  return (
    <div style={{...sx}}>
      <Tooltip title="Select Agency">
        <IconButton onClick={handleOpenUserMenu} sx={{p: 0}}>
          <StyledTitleText>{selectedName}</StyledTitleText>
          <ArrowDropDownIcon sx={{color: '#8A8A8A'}} />
        </IconButton>
      </Tooltip>
      <Menu
        sx={{mt: '45px'}}
        id="menu-appbar"
        anchorEl={anchor}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchor)}
        onClose={handleCloseUserMenu}>
        {availableAgencies.map((item) => {
          return (
            <StyledMenuItem
              key={item.id}
              onClick={() => {
                void handleClick(item.id)
              }}>
              <StyledText
                textAlign="center"
                className="itemText"
                $selected={item.id === loggedInAgencyId}>
                {item.name}
              </StyledText>
            </StyledMenuItem>
          )
        })}
      </Menu>
    </div>
  )
}

export default SelectAgencyMenu
